import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Check, X, Copy, Linkedin, Twitter, Newspaper, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import ArticlePreview from './ArticlePreview';
import { listDrafts, approveDraft, discardDraft } from './api';

const AdminReview = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const drafts = await listDrafts();
        const found = drafts.find((d) => d.id === articleId);
        if (!found) throw new Error('Draft not found, it may have been approved or discarded already.');
        setDraft(found);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [articleId]);

  const handleApprove = async () => {
    if (!draft) return;
    if (!window.confirm(`Publish "${draft.title}"?\n\nIt will appear immediately at /resources/${draft.slug}.`)) return;
    setBusy(true);
    try {
      await approveDraft(draft.id);
      toast.success('Article published', { description: `Live at /resources/${draft.slug}` });
      navigate('/admin');
    } catch (err) {
      toast.error('Publish failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    if (!draft) return;
    if (!window.confirm('Discard this draft? The topic will be released back to the queue.')) return;
    setBusy(true);
    try {
      await discardDraft(draft.id);
      toast.success('Draft discarded');
      navigate('/admin');
    } catch (err) {
      toast.error('Discard failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  const articleUrl = draft ? `https://keystoneconsulting.uk/resources/${draft.slug}` : '';

  const copyLinkedIn = async () => {
    try {
      await navigator.clipboard.writeText(draft.linkedin_post || '');
      toast.success('LinkedIn post copied');
    } catch { toast.error('Copy failed'); }
  };
  const copyTweet = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success('Tweet copied'); }
    catch { toast.error('Copy failed'); }
  };
  const copyPitch = async (target, subject) => {
    const body = (target.pitch_email || '').replace('{article_url}', articleUrl);
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      toast.success(`Pitch for ${target.publication} copied`);
    } catch { toast.error('Copy failed'); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center text-sm">Loading draft…</div>;
  }

  const tweets = Array.isArray(draft?.twitter_thread)
    ? draft.twitter_thread.map((t) => t.replace('{article_url}', articleUrl))
    : [];
  const pubTargets = Array.isArray(draft?.publication_targets) ? draft.publication_targets : [];
  const pubSubjects = Array.isArray(draft?.publication_subject_lines) ? draft.publication_subject_lines : [];

  // The preview component uses camelCase (readMinutes). Backend returns snake_case (read_minutes).
  const previewArticle = draft ? {
    ...draft,
    readMinutes: draft.read_minutes || draft.readMinutes || 6,
  } : null;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 sticky top-0 bg-slate-950/85 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="admin-review-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="text-xs text-slate-500 truncate max-w-md">{draft?.slug}</div>
          <div className="flex items-center gap-2">
            <button onClick={handleDiscard} disabled={busy || !!error} className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-red-300 border border-slate-800 hover:border-red-900 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50" data-testid="admin-review-reject">
              <X className="w-3.5 h-3.5" /> Discard
            </button>
            <button onClick={handleApprove} disabled={busy || !!error} className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-500 rounded-md px-4 py-1.5 font-medium transition-colors disabled:opacity-50" data-testid="admin-review-approve">
              <Check className="w-3.5 h-3.5" /> {busy ? 'Publishing…' : 'Approve & publish'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {error && (
          <div className="mb-6 border border-red-900/50 bg-red-950/40 rounded-lg p-4 text-sm text-red-200 flex items-start gap-2" data-testid="admin-review-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {previewArticle && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Preview, as it will appear at /resources/{draft.slug}</div>
              <ArticlePreview article={previewArticle} />
            </div>

            <aside className="space-y-6">
              <div className="border border-slate-800 rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-1.5 text-sm font-medium">
                    <Linkedin className="w-4 h-4 text-blue-400" /> LinkedIn post
                  </div>
                  <button onClick={copyLinkedIn} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-slate-800 rounded-md px-2.5 py-1 transition-colors" data-testid="admin-review-copy-linkedin">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed max-h-[520px] overflow-y-auto pr-2 font-[Georgia,serif]">
                  {draft.linkedin_post}
                </div>
              </div>

              {tweets.length > 0 && (
                <div className="border border-slate-800 rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="inline-flex items-center gap-1.5 text-sm font-medium">
                      <Twitter className="w-4 h-4 text-sky-400" /> X / Twitter thread
                    </div>
                    <span className="text-xs text-slate-500">{tweets.length} tweets</span>
                  </div>
                  <ol className="space-y-3 max-h-[520px] overflow-y-auto pr-2">
                    {tweets.map((t, i) => (
                      <li key={i} className="border border-slate-800/70 rounded-md p-3 bg-slate-950/40">
                        <div className="flex items-center justify-between mb-2 text-xs text-slate-500">
                          <span>Tweet {i + 1}/{tweets.length} · {t.length} chars</span>
                          <button onClick={() => copyTweet(t)} className="inline-flex items-center gap-1 text-slate-400 hover:text-white" data-testid={`admin-review-copy-tweet-${i}`}>
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                        </div>
                        <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{t}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {pubTargets.length > 0 && (
                <div className="border border-slate-800 rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="inline-flex items-center gap-1.5 text-sm font-medium">
                      <Newspaper className="w-4 h-4 text-amber-400" /> Publication pitches
                    </div>
                    <span className="text-xs text-slate-500">{pubTargets.length} target{pubTargets.length === 1 ? '' : 's'}</span>
                  </div>
                  <ol className="space-y-4 max-h-[640px] overflow-y-auto pr-2">
                    {pubTargets.map((target, i) => {
                      const subject = pubSubjects[i] || '';
                      const body = (target.pitch_email || '').replace('{article_url}', articleUrl);
                      return (
                        <li key={i} className="border border-slate-800/70 rounded-md p-3 bg-slate-950/40">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="text-sm font-medium text-white truncate">{target.publication}</div>
                            <button onClick={() => copyPitch(target, subject)} className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white border border-slate-800 rounded-md px-2 py-0.5 transition-colors" data-testid={`admin-review-copy-pitch-${i}`}>
                              <Copy className="w-3 h-3" /> Copy email
                            </button>
                          </div>
                          <div className="text-xs text-slate-400 mb-2 leading-relaxed"><em>Angle:</em> {target.story_angle}</div>
                          <div className="text-xs text-slate-500 mb-1">Subject</div>
                          <div className="text-sm text-slate-200 mb-3 bg-slate-900/60 border border-slate-800 rounded px-2 py-1.5">{subject}</div>
                          <div className="text-xs text-slate-500 mb-1">Body</div>
                          <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto pr-1 font-[Georgia,serif]">{body}</div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}

              <div className="border border-slate-800 rounded-lg p-5 text-sm space-y-3">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Metadata</div>
                <div><div className="text-slate-500 text-xs">Slug</div><code className="text-slate-200">{draft.slug}</code></div>
                <div><div className="text-slate-500 text-xs">Category</div><div className="text-slate-200">{draft.topic_category || '—'}</div></div>
                <div><div className="text-slate-500 text-xs">Drafted</div><div className="text-slate-200">{draft.created_at?.slice(0, 16).replace('T', ' ')} UTC</div></div>
                <div>
                  <div className="text-slate-500 text-xs">Live URL (after publish)</div>
                  <a href={`/resources/${draft.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 break-all">
                    /resources/{draft.slug} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminReview;
